import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { resolveImageUrl } from '../utils/resolveImageUrl';

const AuthContext = createContext(null);

function isTokenExpired(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('candidate_token');
    const stored = localStorage.getItem('candidate_profile');
    const candidateId = localStorage.getItem('candidate_id');

    if (token && !isTokenExpired(token) && stored) {
      try {
        const profile = JSON.parse(stored);
        setUserToken(token);
        setUser({ ...profile, imageUrl: resolveImageUrl(profile.imageUrl), candidateId: candidateId || null });
      } catch {
        localStorage.removeItem('candidate_token');
        localStorage.removeItem('candidate_id');
        localStorage.removeItem('candidate_profile');
      }
    } else if (token) {
      localStorage.removeItem('candidate_token');
      localStorage.removeItem('candidate_id');
      localStorage.removeItem('candidate_profile');
    }
    setLoading(false);
  }, []);

  // Listen for forced logout from API layer (401)
  useEffect(() => {
    const handler = () => {
      setUserToken(null);
      setUser(null);
    };
    window.addEventListener('candidate-logout', handler);
    return () => window.removeEventListener('candidate-logout', handler);
  }, []);

  const login = useCallback((data) => {
    // data = CandidateLoginResponseModel.data
    localStorage.setItem('candidate_token', data.token);
    if (data.candidateId) {
      localStorage.setItem('candidate_id', data.candidateId);
    }
    const profile = {
      id: data.id,
      email: data.email,
      name: data.name,
      surname: data.surname,
      imageUrl: resolveImageUrl(data.imageUrl),
      provider: data.provider,
    };
    localStorage.setItem('candidate_profile', JSON.stringify(profile));
    setUserToken(data.token);
    setUser({ ...profile, candidateId: data.candidateId || null });
  }, []);

  const updateCandidateId = useCallback((candidateId) => {
    localStorage.setItem('candidate_id', candidateId);
    setUser(prev => prev ? { ...prev, candidateId } : prev);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('candidate_token');
    localStorage.removeItem('candidate_id');
    localStorage.removeItem('candidate_profile');
    setUserToken(null);
    setUser(null);
  }, []);

  const isAuthenticated = !!user && !!userToken && !isTokenExpired(userToken);
  const hasProfile = !!user?.candidateId;

  return (
    <AuthContext.Provider value={{ user, userToken, isAuthenticated, hasProfile, loading, login, logout, updateCandidateId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
